import { Check, AlertTriangle, X, RefreshCw } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';
import type { IntelliSenseStatus } from '../../../connections/services/language-services/languageServiceProvider';

interface IntelliSenseStatusIndicatorProps {
  status: IntelliSenseStatus;
  message?: string;
  className?: string;
  /**
   * Force-refresh the schema snapshot for this tab's connection + database.
   * Only the `partial` state offers it (it is the only state a user can act
   * on); when omitted, `partial` still explains itself but offers no action.
   */
  onReloadSchema?: () => void;
  /** True while a reload triggered from here is still in flight. */
  isReloadingSchema?: boolean;
}

/**
 * Displays the IntelliSense status in a compact, unobtrusive way.
 * - Ready: subtle checkmark (most common state, barely noticeable)
 * - Initializing: small spinner
 * - Partial: energy-hue warning glyph, click-through to what was clipped +
 *   a "Reload schema" action (S7 item 5) — completion works, but an
 *   introspection row cap dropped some objects
 * - Restarting: pulsing refresh icon (database context switch)
 * - Unavailable: X icon
 * - Error: warning icon with tooltip
 */
export function IntelliSenseStatusIndicator({
  status,
  message,
  className = '',
  onReloadSchema,
  isReloadingSchema = false,
}: IntelliSenseStatusIndicatorProps) {
  // Defensive: ensure message is always a string (IPC data may be untyped at runtime)
  const safeMessage = typeof message === 'string' ? message : undefined;

  const getStatusConfig = () => {
    switch (status) {
      case 'ready':
        return {
          icon: Check,
          color: 'text-green-500/70',
          title: 'IntelliSense Ready',
          description: 'Code completion and hints are available for this query.',
        };
      case 'initializing':
        // No `icon` — this state renders RandomDotmSpinner instead of a
        // lucide icon (see the render below), since the spinner has its own
        // props shape (size/dotSize, self-managed animation) rather than a
        // className-driven `animate-spin`.
        return {
          icon: null,
          color: 'text-muted-foreground',
          title: 'IntelliSense Initializing',
          // A manual refresh (Ctrl+Shift+R / explorer Refresh / the pill's
          // Reload schema) reuses this state with its own message.
          description: safeMessage || 'Loading database schema for code completion...',
        };
      case 'partial':
        // Truncation is a "you are not seeing everything" warning, which
        // CH-09 §6 item 7 assigns to the shared energy hue — the same
        // treatment the results grid's truncated row-count already uses.
        // (The `error` arm below stays destructive-red: an error is not a
        // truncation, and CH-28 forbids borrowing energy for it.) Glyph
        // scale, so `--energy`, never the `--energy-text` variant.
        return {
          icon: AlertTriangle,
          color: 'text-energy',
          title: 'Schema Partially Loaded',
          description:
            safeMessage ||
            'Some schema objects exceeded the load cap; completions may be missing.',
        };
      case 'restarting':
        return {
          icon: RefreshCw,
          color: 'text-blue-500',
          title: 'IntelliSense Restarting',
          description: 'Switching database context for PostgreSQL IntelliSense...',
          animate: 'pulse',
        };
      case 'error':
        // CH-28 fix round 1 — this is a static, idle-persistent glyph (no
        // "work in flight" involved), so it must never borrow the --energy
        // family. Destructive-red, matching how transactionIndicator.ts's
        // own error state (`getMessageTypeClass` in MessagesPanel.tsx too)
        // treats an error as its own urgent signal, distinct from energy.
        return {
          icon: AlertTriangle,
          color: 'text-destructive',
          title: 'IntelliSense Error',
          description: safeMessage || 'Failed to connect to the language server.',
        };
      case 'unavailable':
      default:
        return {
          icon: X,
          color: 'text-muted-foreground/50',
          title: 'IntelliSense Unavailable',
          description: 'Connect to a database to enable code completion.',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  const animationClass = config.animate === 'pulse' ? 'animate-pulse' : '';

  // `partial` is the one actionable state, so it gets a click-opened popover
  // instead of a hover tooltip: a tooltip is the wrong host for a button
  // (keyboard-unreachable, dismissed by the pointer travel to it). The
  // message stays available on hover through the trigger's native `title`.
  if (status === 'partial') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="intellisense-partial-trigger"
            title={config.description}
            aria-label={config.title}
            className={`flex items-center gap-1 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
          >
            <AlertTriangle className={`w-3 h-3 ${config.color}`} />
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" align="end" className="w-72 p-3">
          <p className="font-medium text-xs">{config.title}</p>
          <p className="mt-1 text-xs text-muted-foreground">{config.description}</p>
          {onReloadSchema && (
            <Button
              type="button"
              size="xs"
              variant="outline"
              className="mt-3 text-xs"
              data-testid="intellisense-reload-schema"
              disabled={isReloadingSchema}
              onClick={onReloadSchema}
            >
              <RefreshCw className={`w-3 h-3 ${isReloadingSchema ? 'animate-spin' : ''}`} />
              {isReloadingSchema ? 'Reloading…' : 'Reload schema'}
            </Button>
          )}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`flex items-center gap-1 cursor-help ${className}`}>
            {Icon ? (
              <Icon className={`w-3 h-3 ${config.color} ${animationClass}`} />
            ) : (
              <RandomDotmSpinner size={12} className={config.color} />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-medium text-xs">{config.title}</p>
          <p className="text-xs text-primary-foreground/75">{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
